import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Scale, Stethoscope, HardHat, Factory, Truck, HeartHandshake } from "lucide-react";
import { PageHead } from "@/components/PageHead";
import { Reveal } from "@/components/Reveal";
import { CtaSection } from "@/components/CtaSection";
import { INDUSTRIES } from "@/lib/data";

const ICONS = { Scale, Stethoscope, HardHat, Factory, Truck, HeartHandshake };

export default function WhoWeHelp() {
  return (
    <>
      <PageHead
        eyebrow="Who we help"
        title="IT that fits the way your trade works."
        sub="Different businesses break in different ways. Here's how we look after the kinds of organisations we know best."
        testid="who-we-help-title"
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {INDUSTRIES.map((ind, i) => {
            const Icon = ICONS[ind.icon];
            return (
              <Reveal key={ind.id} delay={(i % 3) * 0.08}>
                <article
                  data-testid={`industry-card-${ind.id}`}
                  className="flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5"
                >
                  <span className="mb-5 grid h-11 w-11 place-items-center rounded-xl bg-forest text-blue-300">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="text-lg sm:text-xl font-semibold tracking-tight">{ind.title}</h2>
                  <p className="mt-1 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground">
                    {ind.tagline}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{ind.pain}</p>
                  <ul className="mt-5 pt-5 border-t border-border space-y-2.5">
                    {ind.help.map((h) => (
                      <li key={h} className="flex items-start gap-2.5 text-sm">
                        <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-12 text-center text-sm text-muted-foreground">
            Don't see your industry? If you rely on email, files and a network, we can help.{" "}
            <Link to="/contact" data-testid="industries-contact-link" className="font-semibold text-primary hover:underline underline-offset-4">
              Tell us what you do
            </Link>
            .
          </p>
        </Reveal>
      </section>

      <CtaSection />
    </>
  );
}
